import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { SearchableMultiSelect } from "./SearchableMultiSelect";

const BONUS_PERIODS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
];

function formatDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getRangeForPeriod(period) {
  const today = new Date();
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  if (period === "monthly") {
    start.setDate(1);
  } else if (period === "quarterly") {
    const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
    start.setMonth(quarterStartMonth, 1);
  } else {
    start.setMonth(0, 1);
  }

  return {
    startDate: formatDate(start),
    endDate: formatDate(today),
  };
}

export function CalculateBonusModal({
  show,
  onHide,
  customers,
  loading,
  submitting,
  onSubmit,
}) {
  const [customerCode, setCustomerCode] = useState("");
  const [providerMode, setProviderMode] = useState("all");
  const [selectedProviderCodes, setSelectedProviderCodes] = useState([]);
  const [period, setPeriod] = useState("monthly");

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.code === customerCode),
    [customers, customerCode],
  );

  const providers = useMemo(
    () => selectedCustomer?.providers ?? [],
    [selectedCustomer],
  );
  const providerOptions = useMemo(
    () => providers.map((p) => ({ value: p.code, description: p.name })),
    [providers],
  );
  const dateRange = useMemo(() => getRangeForPeriod(period), [period]);

  useEffect(() => {
    if (!show) return;
    setPeriod("monthly");

    if (customers.length === 1) {
      const singleCustomer = customers[0];
      setCustomerCode(singleCustomer.code);
      setProviderMode("all");
      setSelectedProviderCodes([]);
      return;
    }

    setCustomerCode("");
    setProviderMode("all");
    setSelectedProviderCodes([]);
  }, [show, customers]);

  useEffect(() => {
    if (!customerCode) {
      setProviderMode("all");
      setSelectedProviderCodes([]);
      return;
    }

    setSelectedProviderCodes((prev) =>
      prev.filter((code) => providers.some((p) => p.code === code)),
    );
  }, [customerCode, providers]);

  const selectedProviderNames = useMemo(
    () =>
      providers
        .filter((p) => selectedProviderCodes.includes(p.code))
        .map((p) => p.name),
    [providers, selectedProviderCodes],
  );

  const providerSummary = useMemo(() => {
    if (!customerCode) return "Select a customer first.";
    if (providerMode === "all") return "All providers selected.";
    if (selectedProviderNames.length === 0) return "No providers selected.";
    return `Selected (${selectedProviderNames.length}): ${selectedProviderNames.join(", ")}`;
  }, [customerCode, providerMode, selectedProviderNames]);

  const canSubmit =
    !loading &&
    !submitting &&
    customerCode &&
    (providerMode === "all" || selectedProviderCodes.length > 0) &&
    period;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    await onSubmit({
      customerCode,
      providerCode: null,
      providerCodes: providerMode === "all" ? [] : selectedProviderCodes,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      style={{ overflow: "hidden" }}
      dialogClassName="calculate-bonus-modal"
      backdrop="static"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Calculate Bonus</Modal.Title>
        </Modal.Header>

        <Modal.Body
          style={{ overflow: "visible", maxHeight: "70vh", overflowY: "auto" }}
        >
          {loading ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className="me-2"
              />
              <span>Loading customer profile...</span>
            </div>
          ) : (
            <>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group controlId="bonusCustomerCode">
                    <Form.Label>Customer</Form.Label>
                    <Form.Select
                      value={customerCode}
                      onChange={(e) => setCustomerCode(e.target.value)}
                      disabled={
                        submitting ||
                        customers.length === 0 ||
                        customers.length === 1
                      }
                    >
                      <option value="">Select customer</option>
                      {customers.map((customer) => (
                        <option key={customer.code} value={customer.code}>
                          {customer.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="bonusProviders">
                    <Form.Label>Providers</Form.Label>
                    <div className="d-flex gap-3 mb-2">
                      <Form.Check
                        inline
                        type="radio"
                        id="bonusProvidersAll"
                        label="All providers"
                        name="bonusProviderMode"
                        checked={providerMode === "all"}
                        onChange={() => setProviderMode("all")}
                        disabled={submitting || !customerCode}
                      />
                      <Form.Check
                        inline
                        type="radio"
                        id="bonusProvidersSelected"
                        label="Select providers"
                        name="bonusProviderMode"
                        checked={providerMode === "selected"}
                        onChange={() => setProviderMode("selected")}
                        disabled={
                          submitting ||
                          !customerCode ||
                          providerOptions.length === 0
                        }
                      />
                    </div>

                    <SearchableMultiSelect
                      options={providerOptions}
                      placeholder="Search providers..."
                      value={selectedProviderCodes}
                      onChange={setSelectedProviderCodes}
                      loading={false}
                      disabled={
                        submitting ||
                        !customerCode ||
                        providerMode !== "selected" ||
                        providerOptions.length === 0
                      }
                    />
                    <div className="form-text">{providerSummary}</div>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="bonusPeriod">
                    <Form.Label>Period</Form.Label>
                    <Form.Select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      disabled={submitting}
                    >
                      {BONUS_PERIODS.map((bonusPeriod) => (
                        <option
                          key={bonusPeriod.value}
                          value={bonusPeriod.value}
                        >
                          {bonusPeriod.label}
                        </option>
                      ))}
                    </Form.Select>
                    <div className="form-text">
                      Date range: {dateRange.startDate} to {dateRange.endDate}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {submitting
              ? "Submitting..."
              : providerMode === "all"
                ? "Submit (All providers)"
                : `Submit (${selectedProviderCodes.length} selected)`}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
